// # Protected Route Component
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../auth/state';
import sessionManager from '../auth/session';
import Loading from '../components/common/Loading';

// # Define public routes
const PUBLIC_ROUTES = new Set([
  '/signin', 
  '/signup', 
  '/products', 
  '/team', 
  '/samples', 
  '/welcome',
  '/signin-maintenance',
  '/signup-maintenance'
]);

// # Define subscription-protected routes and their required subscriptions
const SUBSCRIPTION_ROUTES = {
  '/step1-library': ['USMLE Step 1 Library', 'USMLE Step 1 Mentorship'],
  '/step2-library': ['USMLE Step 2 Library', 'USMLE Step 2 Mentorship'],
  '/chapter-iframe': [] // # Will be checked dynamically based on chapter parameter
};

// # Define routes that don't require profile completion
const PROFILE_EXEMPT_ROUTES = new Set(['/signup', '/signout']);

// # Define role-specific routes
const ROLE_ROUTES = {
  'admin': ['/admin'],
  'tutor': ['/tutor-dashboard', '/tutor'],
  'content_reviewer': ['/content-reviewer-dashboard'],
  'financial_manager': ['/financial-manager-dashboard'],
  'designer': ['/designer-dashboard'],
  'support': ['/support-dashboard']
};

function ProtectedRoute({ children, adminOnly = false, requiredRole = null }) {
  const { 
    isAuthenticated, 
    isAdmin, 
    isTutor,
    isContentReviewer,
    isFinancialManager,
    isDesigner,
    isSupport,
    userRole,
    loading, 
    isProfileComplete,
    user 
  } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);

  // # Skip loading state if coming from signin with bypass flag
  const bypassLoading = location.state?.bypassLoading;

  // # Memoize route checks to prevent unnecessary recalculations
  const routeChecks = useMemo(() => ({
    isPublicRoute: PUBLIC_ROUTES.has(location.pathname),
    isRootRoute: location.pathname === '/',
    isSignupRoute: location.pathname === '/signup',
    isSettingsRoute: location.pathname.includes('/settings'),
    isAdminRoute: location.pathname.startsWith('/admin'),
    isTutorRoute: location.pathname.startsWith('/tutor-dashboard') || location.pathname.startsWith('/tutor'),
    isContentReviewerRoute: location.pathname.startsWith('/content-reviewer-dashboard'),
    isFinancialManagerRoute: location.pathname.startsWith('/financial-manager-dashboard'),
    isDesignerRoute: location.pathname.startsWith('/designer-dashboard'),
    isSupportRoute: location.pathname.startsWith('/support-dashboard'),
    requiresProfile: !PROFILE_EXEMPT_ROUTES.has(location.pathname)
  }), [location.pathname]);

  // # Check if user has required role
  const hasRequiredRole = useMemo(() => {
    if (!requiredRole) return true;
    
    switch (requiredRole) {
      case 'admin':
        return isAdmin;
      case 'tutor':
        return isTutor;
      case 'content_reviewer':
        return isContentReviewer;
      case 'financial_manager':
        return isFinancialManager;
      case 'designer':
        return isDesigner;
      case 'support':
        return isSupport;
      default:
        return false;
    }
  }, [requiredRole, isAdmin, isTutor, isContentReviewer, isFinancialManager, isDesigner, isSupport]);

  // # Check subscription access with caching
  useEffect(() => {
    const checkAccess = async () => {
      if (!isAuthenticated || !SUBSCRIPTION_ROUTES[location.pathname]) {
        setAccessChecked(true);
        return;
      }

      try {
        // # Admins and tutors have full access to library and mentorship resources
        if (isAdmin || (isTutor && location.pathname.includes('library') || location.pathname.includes('mentorship'))) {
          setHasAccess(true);
          setAccessChecked(true);
          return;
        }

        const path = location.pathname;
        
        if (path.includes('chapter-iframe')) {
          const params = new URLSearchParams(location.search);
          const chapter = params.get('chapter');
          
          if (!chapter) {
            setHasAccess(false);
            setAccessChecked(true);
            return;
          }

          const contentType = chapter.startsWith('step1_') ? 'step1_library' : 
                            chapter.startsWith('step2_') ? 'step2_library' : null;
          
          if (!contentType) {
            setHasAccess(false);
            setAccessChecked(true);
            return;
          }

          // # Tutors have access to all library content
          if (isTutor) {
            setHasAccess(true);
            setAccessChecked(true);
            return;
          }

          // # Use cached subscription check
          const access = await sessionManager.checkContentAccess(contentType, user?.uid);
          setHasAccess(access);
          setAccessChecked(true);
          return;
        }

        // # Check other subscription routes using cache
        const requiredSubs = SUBSCRIPTION_ROUTES[path];
        if (!requiredSubs) {
          setHasAccess(true);
          setAccessChecked(true);
          return;
        }

        // # Tutors have access to all library and mentorship content
        if (isTutor && (path.includes('library') || path.includes('mentorship'))) {
          setHasAccess(true);
          setAccessChecked(true);
          return;
        }

        // # Use cached subscription data instead of fetching again
        const subscription = await sessionManager.getSession()?.user?.subscriptions;
        const access = subscription?.subscriptionStatus === 'active' && 
                      requiredSubs.includes(subscription?.subscriptionType);
        
        setHasAccess(access);
        setAccessChecked(true);
      } catch (error) {
        console.error('Error checking access:', error);
        setHasAccess(false);
        setAccessChecked(true);
      }
    };

    checkAccess();
  }, [isAuthenticated, isAdmin, isTutor, location.pathname, location.search, user?.uid]);

  // # Handle role-based redirection
  useEffect(() => {
    if (isAuthenticated && !loading && location.pathname !== '/welcome') {
      // # Redirect to role-specific dashboard if on root or dashboard
      if (location.pathname === '/' || location.pathname === '/dashboard') {
        if (isAdmin && !routeChecks.isAdminRoute) {
          navigate('/admin');
          return;
        } else if (isTutor && !routeChecks.isTutorRoute) {
          navigate('/tutor');
          return;
        } else if (isContentReviewer && !routeChecks.isContentReviewerRoute) {
          navigate('/content-reviewer-dashboard');
          return;
        } else if (isFinancialManager && !routeChecks.isFinancialManagerRoute) {
          navigate('/financial-manager-dashboard');
          return;
        } else if (isDesigner && !routeChecks.isDesignerRoute) {
          navigate('/designer-dashboard');
          return;
        } else if (isSupport && !routeChecks.isSupportRoute) {
          navigate('/support-dashboard');
          return;
        }
      }
      
      // # Prevent access to other role dashboards
      if (routeChecks.isAdminRoute && !isAdmin) {
        navigate('/dashboard');
        return;
      } else if (routeChecks.isTutorRoute && !isTutor) {
        navigate('/dashboard');
        return;
      } else if (routeChecks.isContentReviewerRoute && !isContentReviewer) {
        navigate('/dashboard');
        return;
      } else if (routeChecks.isFinancialManagerRoute && !isFinancialManager) {
        navigate('/dashboard');
        return;
      } else if (routeChecks.isDesignerRoute && !isDesigner) {
        navigate('/dashboard');
        return;
      } else if (routeChecks.isSupportRoute && !isSupport) {
        navigate('/dashboard');
        return;
      }
    }
  }, [
    isAuthenticated, 
    isAdmin, 
    isTutor, 
    isContentReviewer, 
    isFinancialManager, 
    isDesigner, 
    isSupport,
    routeChecks, 
    loading, 
    navigate, 
    location.pathname
  ]);

  // # Show loading state only if needed
  if ((loading || !accessChecked) && !bypassLoading) {
    return <Loading message="Checking access..." />;
  }

  // # Handle welcome screen for both admin and regular users
  if (location.pathname === '/welcome') {
    return children;
  }

  // # Handle root route with function children
  if (typeof children === 'function') {
    return children(isAuthenticated);
  }

  // # Handle settings routes
  if (routeChecks.isSettingsRoute) {
    if (location.pathname.startsWith('/admin/settings') && !isAdmin) {
      return <Navigate to="/dashboard" replace />;
    }
    if (!location.pathname.startsWith('/admin/settings') && isAdmin) {
      return <Navigate to="/admin/settings" replace />;
    }
    return children;
  }

  // # Handle root route
  if (routeChecks.isRootRoute && isAuthenticated && isProfileComplete) {
    // # Redirect to appropriate dashboard based on role
    if (isAdmin) {
      return <Navigate to="/admin" replace />;
    } else if (isTutor) {
      return <Navigate to="/tutor" replace />;
    } else if (isContentReviewer) {
      return <Navigate to="/content-reviewer-dashboard" replace />;
    } else if (isFinancialManager) {
      return <Navigate to="/financial-manager-dashboard" replace />;
    } else if (isDesigner) {
      return <Navigate to="/designer-dashboard" replace />;
    } else if (isSupport) {
      return <Navigate to="/support-dashboard" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // # Handle profile completion check
  if (isAuthenticated && !isProfileComplete && routeChecks.requiresProfile) {
    // # Check if we have profile completion status in location state
    const stateProfileComplete = location.state?.isProfileComplete;
    
    // # Log the profile check
    console.log('Profile completion check:', {
      isAuthenticated,
      isProfileComplete,
      stateProfileComplete,
      requiresProfile: routeChecks.requiresProfile,
      pathname: location.pathname
    });

    // # Use state value if available
    if (typeof stateProfileComplete === 'boolean' && stateProfileComplete) {
      return children;
    }

    return <Navigate to="/signup" state={{ 
      message: "Please complete your profile to continue",
      email: user?.email,
      step: 2
    }} replace />;
  }

  // # Handle authenticated users on public routes
  if (isAuthenticated && isProfileComplete && routeChecks.isPublicRoute) {
    // # Allow access to welcome screen even when authenticated
    if (location.pathname === '/welcome') {
      return children;
    }
    
    // # Redirect to appropriate dashboard based on role
    if (isAdmin) {
      return <Navigate to="/admin" replace />;
    } else if (isTutor) {
      return <Navigate to="/tutor" replace />;
    } else if (isContentReviewer) {
      return <Navigate to="/content-reviewer-dashboard" replace />;
    } else if (isFinancialManager) {
      return <Navigate to="/financial-manager-dashboard" replace />;
    } else if (isDesigner) {
      return <Navigate to="/designer-dashboard" replace />;
    } else if (isSupport) {
      return <Navigate to="/support-dashboard" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // # Check admin access
  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // # Check role-specific access
  if (requiredRole && !hasRequiredRole) {
    return <Navigate to="/dashboard" replace />;
  }

  // # Check subscription access
  if (isAuthenticated && SUBSCRIPTION_ROUTES[location.pathname] && !hasAccess) {
    return <Navigate to="/products" state={{ 
      message: "You need an active subscription to access this content"
    }} replace />;
  }

  // # Handle unauthenticated users
  if (!isAuthenticated && !routeChecks.isPublicRoute && !routeChecks.isRootRoute) {
    return <Navigate to="/signin" state={{ from: location.pathname }} replace />;
  }

  // # Render protected content
  return children;
}

export default ProtectedRoute; 